import HttpClient from '@/api/HttpClient';
import axios, { type AxiosError } from 'axios';

const httpClient = new HttpClient();

const API_URL: string = import.meta.env.VITE_APP_SERVER_DOMAIN || 'https://endpointnotfound';

export type IaStoreCountReportRow = {
  countDate: string;
  locationId: number;
  locationName: string;
  upc: string;
  itemName: string | null;
  uomPackType: string;
  size: string;
  quantity: number;
  catalogOrigin: string;
  lastUpdated: string | null;
};

export type IaStoreCountReportTotals = {
  locationCount: number;
  countDayCount: number;
  lineCount: number;
  totalUnits: number;
};

async function parseBlobError(err: unknown): Promise<never> {
  const ax = err as AxiosError;
  const data = ax.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text) as { error?: string; message?: string };
      throw new Error(json.error || json.message || 'Export failed');
    } catch (e) {
      if (e instanceof Error && e.message !== 'Export failed' && !e.message.includes('JSON')) {
        throw e;
      }
      throw new Error('Export failed');
    }
  }
  const d = data as { error?: string; message?: string } | undefined;
  throw new Error(d?.error || d?.message || (err instanceof Error ? err.message : 'Export failed'));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const m = /filename="([^"]+)"/i.exec(header) || /filename=([^;]+)/i.exec(header);
  return m?.[1]?.trim() || fallback;
}

async function postCsvExport(
  resource: string,
  body: Record<string, unknown>,
  fallbackFilename: string,
): Promise<void> {
  const jwt = window.localStorage.getItem('jwt');
  try {
    const response = await axios.post(`${API_URL}/${resource}`, body, {
      responseType: 'blob',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    });
    const blob = response.data as Blob;
    if (blob.type && blob.type.includes('application/json')) {
      const text = await blob.text();
      const json = JSON.parse(text) as { error?: string; message?: string };
      throw new Error(json.error || json.message || 'Export failed');
    }
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      fallbackFilename,
    );
    downloadBlob(blob, filename);
  } catch (e) {
    await parseBlobError(e);
  }
}

export async function fetchIaStoreCountDates(params?: {
  locationId?: number;
}): Promise<{ dates: string[] }> {
  const response = await httpClient.post('api/inventory-auditor/reports/store-counts/dates', params ?? {});
  return response.data as { dates: string[] };
}

export async function queryIaStoreCountsReport(params: {
  countDate: string;
  locationId?: number;
  skip?: number;
  take?: number;
}): Promise<{
  rows: IaStoreCountReportRow[];
  total: number;
  totals: IaStoreCountReportTotals;
}> {
  const response = await httpClient.post('api/inventory-auditor/reports/store-counts', params);
  return response.data as {
    rows: IaStoreCountReportRow[];
    total: number;
    totals: IaStoreCountReportTotals;
  };
}

export async function exportIaStoreCountsReport(params: {
  countDate: string;
  locationId?: number;
}): Promise<void> {
  await postCsvExport(
    'api/inventory-auditor/reports/store-counts/export',
    params,
    `inventory-auditor-store-counts-${params.countDate}.csv`,
  );
}
